import { reactive } from 'vue';
import { isDebugModeActive } from '@/composables/debug/useDebugMode';
import { sanitizeHtml, sanitizeInlineHtml } from '@/utils/sanitizeHtml';

type TranslationsDictionary = Record<string, string>;
export type TranslationParams = Record<string, string | number | boolean>;

declare const ibeTranslations: TranslationsDictionary | undefined;

const dictionary: TranslationsDictionary = reactive({});

const loadIbeTranslations = (): void => {
  const globalTranslations = ibeTranslations;

  if (typeof globalTranslations === 'object' && globalTranslations !== null) {
    Object.assign(dictionary, globalTranslations as TranslationsDictionary);
  }
};

loadIbeTranslations();

/**
 * Interpolates placeholders in a string with provided values.
 * Placeholders use {key} syntax, e.g., "Hello {name}" with { name: "World" } => "Hello World"
 */
const interpolate = (text: string, params?: TranslationParams): string => {
  if (!params) return text;

  return text.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = params[key];
    return value !== undefined ? String(value) : match;
  });
};

/**
 * Resolves a key to its interpolated string. Shared by $t and $tHtml.
 */
const resolve = (key: string, fallback: string = key, params?: TranslationParams): string => {
  const translation = dictionary[key];

  // In debug mode, show key when translation is missing
  if (!translation && isDebugModeActive()) {
    return `[${key}]`;
  }

  const text = translation || fallback;
  return interpolate(text, params);
};

/**
 * Translates a key to its localized string with optional interpolation.
 *
 * Returns PLAIN text — use for `{{ }}` interpolation and attributes. For
 * translations containing HTML (formatting, links) use {@link $tHtml} instead.
 *
 * @param key - The translation key (e.g., 'ibe.checkout.traveler.adult-title')
 * @param fallback - Fallback text if key not found (defaults to key)
 * @param params - Optional object with values to interpolate (e.g., { number: 1 })
 * @returns Translated and interpolated string
 *
 * @example
 * // Simple usage
 * $t('button.submit', 'Submit')
 *
 * @example
 * // With interpolation
 * $t('ibe.checkout.traveler.adult-title', 'Traveler {number}', { number: 1 })
 * // Returns: "Traveler 1"
 */
export const $t = (key: string, fallback: string = key, params?: TranslationParams): string =>
  resolve(key, fallback, params);

/**
 * Translates a key to a SANITIZED HTML string, intended for `v-html`.
 *
 * Allows inline formatting and links (`strong`, `em`, `a`, …); everything else is
 * stripped via {@link sanitizeInlineHtml}. Sanitization runs after interpolation,
 * so interpolated values (e.g. URLs) are cleaned too. For template ergonomics,
 * prefer the `<I18nHtml>` component which wraps this.
 *
 * @param key - The translation key (e.g., 'ibe.terms.privacy-policy')
 * @param fallback - Fallback text if key not found (defaults to key)
 * @param params - Optional object with values to interpolate (e.g., { url })
 * @returns Sanitized HTML string safe for v-html
 *
 * @example
 * // <span v-html="$tHtml('ibe.terms.privacy', '… <a href=\"{url}\">policy</a>', { url })"></span>
 */
export const $tHtml = (key: string, fallback: string = key, params?: TranslationParams): string =>
  sanitizeInlineHtml(resolve(key, fallback, params));

/**
 * Matches any HTML tag, e.g. `<p>`, `</ul>`, `<br />`. Used to tell an
 * authored-HTML translation apart from a plain-text one.
 */
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;

/**
 * Translates a key to a SANITIZED **block-level** HTML string, intended for `v-html`.
 *
 * Same as {@link $tHtml} but keeps block tags (`p`, `ul`, `li`, `h1`-`h6`, …) via
 * {@link sanitizeHtml}. Use for longer editorial translations that CMS editors
 * format themselves; for short inline labels and links prefer {@link $tHtml}.
 *
 * Plain-text translations (no HTML tags) get their newlines converted to `<br>`,
 * so a value authored before the key was switched to HTML still renders its line
 * breaks. Once the value contains markup, the newlines in it are left to collapse
 * as normal so indentation does not add stray gaps.
 *
 * @param key - The translation key (e.g., 'ibe.modal.more-options-content')
 * @param fallback - Fallback text if key not found (defaults to key)
 * @param params - Optional object with values to interpolate
 * @returns Sanitized HTML string safe for v-html
 *
 * @example
 * // <div v-html="$tHtmlBlock('ibe.modal.more-options-content', '<p>…</p>')"></div>
 */
export const $tHtmlBlock = (
  key: string,
  fallback: string = key,
  params?: TranslationParams
): string => {
  const text = resolve(key, fallback, params);
  const withBreaks = HTML_TAG_PATTERN.test(text) ? text : text.replace(/\n/g, '<br>');

  return sanitizeHtml(withBreaks);
};

export { dictionary, loadIbeTranslations };
